import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from './services/auth.service';
import { AuditService } from "./services/audit.service";
import { ThemeService } from './core/theme/theme.service';
import { BrandingService } from './core/branding/branding.service';
import { filter, takeUntil } from 'rxjs/operators';
import { fromEvent, Subject, Subscription } from 'rxjs';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'SafeRoads Kane County - Admin Portal';
  showNavigation = false;
  private destroy$ = new Subject<void>();
  private remountInProgress = false;
  private restoreInFlight = false;
  private restoreSub: Subscription | null = null;

  constructor(
    private router: Router,
    private authService: AuthService,
    private audit: AuditService,
    private themeService: ThemeService,
    private brandingService: BrandingService
  ) { }

  ngOnInit() {
    this.audit.init();
    void this.themeService.loadAndApply();
    void this.brandingService.loadAndApply();

    this.brandingService.branding$
      .pipe(takeUntil(this.destroy$))
      .subscribe((branding) => {
        const title = branding.programTitle?.trim();
        if (title) {
          document.title = title;
        }
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.updateNavigationVisibility(event.url);
      });

    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateNavigationVisibility(this.router.url);
      });

    fromEvent(document, 'visibilitychange')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (document.visibilityState === 'visible') {
          this.restoreSessionIfExpired();
        }
      });

    fromEvent(window, 'focus')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.restoreSessionIfExpired());

    fromEvent(window, 'pageshow')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: Event) => {
        const pageEvent = event as PageTransitionEvent;
        if (pageEvent.persisted) {
          this.restoreSessionIfExpired();
        }
      });

    queueMicrotask(() => {
      this.updateNavigationVisibility(this.router.url);
    });
  }

  ngOnDestroy() {
    this.restoreSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * On return to the app, only recover the session when auth actually needs it.
   * If access is still valid, do nothing. If access expired, refresh and then
   * remount so page-level loaders fetch fresh data. If refresh fails, go login.
   */
  private restoreSessionIfExpired(): void {
    if (this.router.url.includes('/login')) {
      return;
    }

    if (!this.authService.isAuthenticated()) {
      this.navigateToLogin();
      return;
    }

    const accessExpired =
      this.authService.isTokenExpired() || !this.authService.getToken();
    if (!accessExpired) {
      return;
    }

    if (this.restoreInFlight) {
      return;
    }

    this.restoreInFlight = true;
    this.restoreSub?.unsubscribe();
    this.restoreSub = this.authService.ensureValidSession().subscribe({
      next: (ok) => {
        this.restoreInFlight = false;

        if (!ok) {
          this.navigateToLogin();
          return;
        }

        this.remountCurrentRoute();
      },
      error: () => {
        this.restoreInFlight = false;
        this.navigateToLogin();
      },
    });
  }

  private navigateToLogin(): void {
    if (this.router.url.includes('/login')) {
      return;
    }
    this.router.navigate(['/login'], {
      queryParams: { returnUrl: this.router.url },
    });
  }

  private remountCurrentRoute(): void {
    if (this.remountInProgress) {
      return;
    }
    const url = this.router.url;
    if (!url || url.includes('/login')) {
      return;
    }

    this.remountInProgress = true;
    this.router
      .navigateByUrl('/', { skipLocationChange: true })
      .then(() => this.router.navigateByUrl(url))
      .finally(() => {
        this.remountInProgress = false;
      });
  }

  private updateNavigationVisibility(url: string) {
    const isLoginPage = url.includes('/login');
    const isUserGuide = url.includes('/user-guide');
    const isAuthenticated = this.authService.isAuthenticated();

    this.showNavigation = isAuthenticated && !isLoginPage && !isUserGuide;
  }
}
